"use client";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import GhostBtn from "../GhostBtn";
import ROUTES from "@/lib/routes";
import { usePathname } from "next/navigation";
import { useLocalizedRoute } from "@/app/lib/useLocalizedRoute";
import { useLandingCms } from "@/app/contexts/LandingCmsContext";
import { isGccLandingPath } from "@/app/lib/gccLandingPath";
import FaqAccordionItem from "@/app/components/faq/FaqAccordionItem";

const FAQ: React.FunctionComponent = () => {
  const t = useTranslations("FAQ");
  const pathname = usePathname();
  const { getLocalizedRoute } = useLocalizedRoute();
  const cms = useLandingCms();
  const isGccLandingPage = isGccLandingPath(pathname);

  const [openFaqKey, setOpenFaqKey] = useState<string | null>(null);

  const toggleFaq = (key: string) => {
    setOpenFaqKey((current) => (current === key ? null : key));
  };

  // Helper function to get translation with fallback
  const getTranslation = (key: string, fallback: string) => {
    const translated = t(key);
    if (translated === `FAQ.${key}` || translated.startsWith("FAQ.")) {
      return fallback;
    }
    return translated;
  };

  type FAQItem = {
    key: string;
    questionKey: string;
    questionFallback: string;
    answerKey: string;
    answerFallback: string;
  };

  const faqItemsDefault: FAQItem[] = [
    {
      key: "whatIsPineGen",
      questionKey: "items.whatIsPineGen.question",
      questionFallback: "What is PineGen AI?",
      answerKey: "items.whatIsPineGen.answer",
      answerFallback:
        "PineGen AI is an AI-powered Pine Script code generator built for TradingView. It helps users generate, refine, and explain Pine Script strategies faster using a no-code workflow, along with a live preview chart and backtesting summary.",
    },
    {
      key: "aiCreatePineScript",
      questionKey: "items.aiCreatePineScript.question",
      questionFallback: "Can AI help create Pine Script for TradingView?",
      answerKey: "items.aiCreatePineScript.answer",
      answerFallback:
        "Yes, AI can help create Pine Script for TradingView, and PineGen AI is designed to make that process easier. With PineGen AI, you can turn a trading idea into Pine Script strategy code faster, then use its workflow to refine, test, and improve your TradingView strategy.",
    },
    {
      key: "freeTradingView",
      questionKey: "items.freeTradingView.question",
      questionFallback: "Can I use TradingView Pine Script for free?",
      answerKey: "items.freeTradingView.answer",
      answerFallback:
        "Yes, you can use Pine Script on TradingView with a free TradingView account, and beginners often start that way. However, some TradingView features, alerts, and advanced tools may depend on your plan. Using PineGen AI, you can get help to create TradingView Pine Script faster with AI.",
    },
    {
      key: "commonMistakes",
      questionKey: "items.commonMistakes.question",
      questionFallback: "What are common Pine Script coding mistakes?",
      answerKey: "items.commonMistakes.answer",
      answerFallback:
        "Some of the most common Pine Script mistakes include repainting issues, wrong entry and exit logic, bad risk management settings, misuse of indicators, and syntax errors. Reduce these problems by generating structured Pine Script code that is validated and compiled using PineGen AI.",
    },
    {
      key: "difficulty",
      questionKey: "items.difficulty.question",
      questionFallback: "Is Pine Script hard for beginners to learn?",
      answerKey: "items.difficulty.answer",
      answerFallback:
        "Pine Script can be challenging for beginners at first, especially when learning strategy logic, syntax, and TradingView-specific rules. PineGen AI helps make that easier by giving beginners a simpler way to create, understand, and refine Pine Script strategies using simple prompts.",
    },
    {
      key: "automation",
      questionKey: "items.automation.question",
      questionFallback: "How can I automate a Pine Script strategy?",
      answerKey: "items.automation.answer",
      answerFallback:
        "You can automate a Pine Script strategy by creating strategy logic in TradingView and using alerts to trigger your trading workflow. PineGen AI helps with this by generating Pine Script strategies from a simple prompt, so you can build alert-ready strategy code faster and prepare it for TradingView strategy automation.",
    },
    {
      key: "freePineGen",
      questionKey: "items.freePineGen.question",
      questionFallback: "Is it really free to get started with PineGen AI?",
      answerKey: "items.freePineGen.answer",
      answerFallback:
        "Yes, getting started with PineGen AI is simple and free. New users can explore how to generate Pine Script strategies for TradingView with a single sign-up. Get Started for free: ",
    },
    {
      key: "backtesting",
      questionKey: "items.backtesting.question",
      questionFallback: "How do I backtest a Pine Script strategy?",
      answerKey: "items.backtesting.answer",
      answerFallback:
        "You can backtest a Pine Script strategy in TradingView by applying the strategy to a chart and checking its performance with the Strategy Tester. PineGen AI makes this process easier by helping you create Pine Script strategy code along with a realtime TradingView chart and backtest preview.",
    },
    {
      key: "pineVersion",
      questionKey: "items.pineVersion.question",
      questionFallback: "Which Pine Script version does TradingView support?",
      answerKey: "items.pineVersion.answer",
      answerFallback:
        "TradingView currently uses Pine Script v6, and PineGen AI also uses Pine Script v6 to generate code. This helps users build and improve TradingView strategies using the latest Pine Script environment.",
    },
  ];

  // USA FAQ items
  const faqItemsUSA = [
    {
      question: "What is PineGen AI in USA?",
      answer:
        "PineGen AI is an AI-powered Pine Script generator for TradingView built for users in the United States who want to create strategies faster. It helps you generate code, refine strategy logic, fix errors, and prepare TradingView strategies for backtesting with a simpler no-code approach.",
    },
    {
      question: "Is this Pine Script AI free?",
      answer:
        "PineGen AI offers a free plan that allows users to try the platform at no cost. Using the free version, you can generate and test only a few trading scripts before hitting usage caps. To access more advanced capabilities, upgrade to a paid subscription plan. Get Started for free: ",
    },
    {
      question: "Can AI help create Pine Script for TradingView?",
      answer:
        "Yes, AI can help create Pine Script for TradingView, and PineGen AI is built to make that process easier for users in the United States. It works as a Pine Script AI generator that helps you turn a trading idea into TradingView strategy code faster with a no-code workflow.",
    },
    {
      question: "Is Pine Script hard to learn?",
      answer:
        "Pine Script can feel difficult at first, especially for beginners learning strategy logic, syntax, and TradingView rules. PineGen AI makes that easier by helping you create Pine Script strategies from plain-English prompts, understand the code more clearly, and improve your strategy faster.",
    },
    {
      question: "Can I use AI to predict trades?",
      answer:
        "Not exactly. PineGen AI helps you create, refine, and backtest Pine Script strategies for TradingView, but it does not guarantee winning trades or predict the market with certainty. It is best used to test trading ideas, improve strategy logic, and support better decision-making.",
    },
    {
      question:
        "What is the best AI pine script code generator for tradingview?",
      answer:
        "PineGen AI is built to be a strong option for TradingView users who want to create Pine Script strategies faster. It helps users with features that matter most to strategy builders, including instant code generation, live chart preview, backtesting summary, code explanation, prompt builder, chat continuation, and thread management.",
    },
    {
      question: "Which Pine Script version does TradingView support?",
      answer:
        "TradingView currently uses Pine Script v6, and PineGen AI also uses Pine Script v6 to generate code. This helps users build and improve TradingView strategies using the latest Pine Script environment.",
    },
    {
      question: "Can I backtest strategies created by AI?",
      answer:
        "Yes, PineGen AI helps you create Pine Script strategies for TradingView and review them with backtesting support, so you can see how a strategy performs before making changes. This makes it easier to evaluate results, spot areas to improve, and refine your strategy with more confidence.",
    },
    {
      question: "Can AI fix or debug Pine Script code?",
      answer:
        "Yes, PineGen AI can help you fix and debug Pine Script code by making errors easier to spot and strategy logic easier to refine. It helps you improve TradingView strategies faster with AI-generated code, clearer explanations, and simpler updates.",
    },
    {
      question: "Is Pine Script AI suitable for beginners?",
      answer:
        "Yes, PineGen AI is suitable for beginners because it provides a no-code workflow to create Pine Script strategies using simple English prompts, making it easier to get started and learn.",
    },
  ];

  return (
    <LazyMotion features={domAnimation}>
    <section className="relative w-full  flex flex-col items-center justify-center overflow-hidden py-20 bg-[#191919]">
      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 h-full flex flex-col justify-center">
        {/* Title Section */}
        <div className="text-center">
          <m.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-[#767EF6] mb-3 sm:mb-4 md:mb-6 text-2xl md:text-3xl lg:text-[40px] !leading-8 md:!leading-10 lg:!leading-[55px] text-center mx-auto"
            style={{
              fontStyle: "normal",
              fontWeight: 800,
            }}
          >
            {getTranslation("title", "FAQ")}
          </m.h2>
        </div>

        {/* Descriptive Text */}
        <m.p
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[#FCFAFA] mb-6 max-w-[840px] mx-auto px-4 md:text-lg lg:text-[24px] whitespace-pre-line !leading-6 md:!leading-7 lg:!leading-[40px]"
          style={{
            fontWeight: 600,
          }}
        >
          {getTranslation(
            "description",
            "Everything you need to know about PineGen AI and how it can help you create powerful trading strategies.",
          )}
        </m.p>

        {/* FAQ */}
        <div className="space-y-4 md:space-y-6 mt-12">
          {cms?.faqs?.length
            ? cms.faqs.map((item, index) => (
                <m.div
                  key={`cms-faq-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <FaqAccordionItem
                    id={`landing-cms-faq-${index}`}
                    isOpen={openFaqKey === `cms-${index}`}
                    onToggle={() => toggleFaq(`cms-${index}`)}
                    question={item.question}
                  >
                    <p className="text-white font-semibold md:text-base lg:text-lg text-sm text-left leading-relaxed">
                      {item.answer}
                    </p>
                  </FaqAccordionItem>
                </m.div>
              ))
            : null}
          {!cms?.faqs?.length &&
            !pathname.startsWith("/us") &&
            faqItemsDefault.map((item, index) => (
              <m.div
                key={item.key}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <FaqAccordionItem
                  id={`landing-faq-${index}`}
                  isOpen={openFaqKey === item.key}
                  onToggle={() => toggleFaq(item.key)}
                  question={getTranslation(
                    item.questionKey,
                    item.questionFallback,
                  )}
                >
                  <p className="text-white font-semibold md:text-base lg:text-lg text-sm text-left leading-relaxed">
                    {getTranslation(item.answerKey, item.answerFallback)}
                    {item.key === "freePineGen" && (
                      <a
                        className="text-[#0960FC]"
                        href="https://www.pinegen.ai/login"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://www.pinegen.ai/login
                      </a>
                    )}
                  </p>
                </FaqAccordionItem>
              </m.div>
            ))}

          {
            // US only FAQ
            !cms?.faqs?.length &&
              pathname.startsWith("/us") &&
              faqItemsUSA.map((item, index) => (
                <m.div
                  key={`us-faq-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <FaqAccordionItem
                    id={`landing-us-faq-${index}`}
                    isOpen={openFaqKey === `us-${index}`}
                    onToggle={() => toggleFaq(`us-${index}`)}
                    question={item.question}
                  >
                    <p className="text-white font-semibold md:text-base lg:text-lg text-sm text-left leading-relaxed">
                      {item.answer}
                      {index === 1 && (
                        <a
                          className="text-[#0960FC]"
                          href="https://www.pinegen.ai/login"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://www.pinegen.ai/login
                        </a>
                      )}
                    </p>
                  </FaqAccordionItem>
                </m.div>
              ))
          }
        </div>

        {/* CTA */}
        {!isGccLandingPage && (
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.6,
              delay: faqItemsDefault.length * 0.1 + 0.2,
            }}
            className="w-full flex items-center justify-center mt-14"
          >
            <GhostBtn
              translationNamespace="FAQ"
              translationKey="visitFAQPage"
              fallbackText="Visit FAQ Page"
              route={getLocalizedRoute(ROUTES.FAQ)}
            />
          </m.div>
        )}
      </div>
    </section>
    </LazyMotion>
  );
};

export default FAQ;
